import type { Edge, Node } from '@xyflow/react'
import type { AutomationTurn } from './hooks/graph-automation'
import {
  assertAutomationTurnCurrent,
  AutomationRejectedError,
  dispatchSingleRun,
} from './hooks/graph-automation'
import { buildEngineStrategyGraph } from './utils/engine-view'
import { validateGraphBeforeBacktest } from './utils/validate-graph'
import { validateGraph } from './graph-engine/graph-validator'
import type { GraphEdge, GraphNode } from './schemas/backtest-types'
import type { NodeData } from './types/nodes'
import { singleRunClient } from '@/services/api/api-client'
import { ApiError } from '@/services/api/errors'
import type { SingleRunReceipt } from './hooks/single-run-receipt'

export interface HeadlessRunRequest {
  strategyId: string
  graphRevision: number
  graph: { nodes: GraphNode[]; edges: GraphEdge[] }
  ticker: string
  targetDate: string
  initialCash: number
}

export interface HeadlessRunMetrics {
  totalPnl?: number
  maxDrawdown?: number
  winRate?: number
  totalTrades?: number
  totalReturn?: number
  finalValue?: number
  commissionPaid?: number | null
  locateFeesPaid?: number | null
  slippageCostTotal?: number | null
  executionTimeSeconds?: number
}

/** Raw response remains inside the trusted app for execution analysis. */
export interface HeadlessRunResponse {
  execution_id?: string
  success?: boolean
  pnl?: number
  max_drawdown?: number
  win_rate?: number
  trade_count?: number
  total_return?: number
  final_portfolio_value?: number
  commission_paid?: number | null
  locate_fees_paid?: number | null
  slippage_cost_total?: number | null
  transactions_uri?: string | null
  debug_file_url?: string | null
  execution_time_s?: number
  [key: string]: unknown
}

export interface HeadlessRunReceipt extends SingleRunReceipt {
  strategyId: string
  graphRevision: number
  ticker: string
  targetDate: string
  initialCash: number
  metrics?: HeadlessRunMetrics
}

/**
 * Public receipt plus the unredacted response for trusted in-app analysis.
 * Callers that send a tool result to the model must serialize `receipt` only.
 */
export interface HeadlessRunResult {
  receipt: HeadlessRunReceipt
  response?: HeadlessRunResponse
}

interface HeadlessRunParams {
  strategy_id: string
  strategy: { nodes: Array<Record<string, unknown>>; edges: Array<Record<string, unknown>> }
  ticker: string
  target_date: string
  initial_cash: number
}

function baseReceipt(request: HeadlessRunRequest): HeadlessRunReceipt {
  return {
    status: 'rejected',
    strategyId: request.strategyId,
    graphRevision: request.graphRevision,
    ticker: request.ticker,
    targetDate: request.targetDate,
    initialCash: request.initialCash,
  }
}

function rejected(request: HeadlessRunRequest, message: string): HeadlessRunResult {
  return { receipt: { ...baseReceipt(request), status: 'rejected', message } }
}

function metrics(response: HeadlessRunResponse): HeadlessRunMetrics {
  return {
    totalPnl: response.pnl,
    maxDrawdown: response.max_drawdown,
    winRate: response.win_rate,
    totalTrades: response.trade_count,
    totalReturn: response.total_return,
    finalValue: response.final_portfolio_value,
    commissionPaid: response.commission_paid,
    locateFeesPaid: response.locate_fees_paid,
    slippageCostTotal: response.slippage_cost_total,
    executionTimeSeconds: response.execution_time_s,
  }
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const detail = (error.body as { detail?: unknown } | undefined)?.detail
    if (typeof detail === 'string') return detail
    if (detail && typeof detail === 'object' && 'reason' in detail) {
      const reason = (detail as { reason?: unknown }).reason
      if (typeof reason === 'string') return reason
    }
  }
  return error instanceof Error ? error.message : String(error)
}

function isValidDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const date = new Date(`${value}T00:00:00Z`)
  return (
    !Number.isNaN(date.getTime()) &&
    date.getUTCFullYear() === Number(value.slice(0, 4)) &&
    date.getUTCMonth() + 1 === Number(value.slice(5, 7)) &&
    date.getUTCDate() === Number(value.slice(8, 10))
  )
}

function validateRequest(request: HeadlessRunRequest): string | undefined {
  if (!request.strategyId.trim()) return 'A strategy ID is required.'
  if (!Number.isInteger(request.graphRevision) || request.graphRevision < 0)
    return 'A non-negative graph revision is required.'
  if (!request.graph.nodes.length) return 'The saved strategy graph is empty.'
  if (!request.ticker.trim()) return 'A ticker is required.'
  if (!isValidDate(request.targetDate)) return 'Target date must be YYYY-MM-DD.'
  if (!Number.isFinite(request.initialCash) || request.initialCash <= 0)
    return 'Initial cash must be a positive finite number.'
  return undefined
}

function runKey(request: HeadlessRunRequest): string {
  return JSON.stringify([
    request.strategyId,
    request.graphRevision,
    request.ticker,
    request.targetDate,
    request.initialCash,
  ])
}

export async function runHeadlessSingleRun(
  request: HeadlessRunRequest,
  turn?: AutomationTurn,
): Promise<HeadlessRunResult> {
  const invalid = validateRequest(request)
  if (invalid) return rejected(request, invalid)

  const graph = request.graph
  const validation = validateGraph(graph.nodes, graph.edges)
  if (validation.errors.length > 0) {
    return rejected(request, validation.errors[0]?.message ?? 'Graph validation failed.')
  }
  const runValidation = validateGraphBeforeBacktest(
    graph.nodes as unknown as Node<NodeData>[],
    graph.edges as unknown as Edge[],
  )
  if (runValidation.length > 0) {
    return rejected(request, runValidation[0]?.missingInputs.join(', ') ?? 'Graph is not runnable.')
  }

  const strategy = buildEngineStrategyGraph(
    graph.nodes as unknown as Node[],
    graph.edges as unknown as Edge[],
  )
  if (!strategy.nodes.length) return rejected(request, 'The saved strategy has no runnable nodes.')

  const body: HeadlessRunParams = {
    strategy_id: request.strategyId,
    strategy,
    ticker: request.ticker.trim(),
    target_date: request.targetDate,
    initial_cash: request.initialCash,
  }

  const dispatch = async (): Promise<HeadlessRunResult> => {
    let response: HeadlessRunResponse
    try {
      response = await singleRunClient.post<HeadlessRunResponse>('/run', body, {
        signal: turn?.signal,
        timeoutMs: 320_000,
      })
    } catch (error) {
      if (error instanceof AutomationRejectedError) {
        return {
          receipt: { ...baseReceipt(request), status: 'rejected', message: error.message },
        }
      }
      if (error instanceof ApiError) {
        return {
          receipt: {
            ...baseReceipt(request),
            status: 'failed',
            message: errorMessage(error),
          },
        }
      }
      return {
        receipt: {
          ...baseReceipt(request),
          status: 'unknown',
          message: 'Execution outcome is unknown. Inspect current results before retrying.',
        },
      }
    }

    if (turn) assertAutomationTurnCurrent(turn, undefined, false, { allowHeadless: true })
    if (response.success === false) {
      return {
        receipt: {
          ...baseReceipt(request),
          status: 'failed',
          executionId: response.execution_id,
          message: 'Backtest failed.',
          metrics: metrics(response),
        },
        response,
      }
    }
    if (!response.execution_id) {
      return {
        receipt: {
          ...baseReceipt(request),
          status: 'unknown',
          message: 'Execution completed without an execution ID.',
          metrics: metrics(response),
        },
        response,
      }
    }
    return {
      receipt: {
        ...baseReceipt(request),
        status: 'completed',
        executionId: response.execution_id,
        metrics: metrics(response),
      },
      response,
    }
  }

  try {
    return await dispatchSingleRun(runKey(request), dispatch, turn ?? null, {
      allowHeadless: true,
    })
  } catch (error) {
    if (error instanceof AutomationRejectedError) {
      return {
        receipt: { ...baseReceipt(request), status: 'rejected', message: error.message },
      }
    }
    return {
      receipt: {
        ...baseReceipt(request),
        status: 'unknown',
        message: 'Execution outcome is unknown. Inspect current results before retrying.',
      },
    }
  }
}

export const headlessRunKey = runKey
